import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Col, Row, Card, CardBody, CardTitle, Container } from "reactstrap";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { complianceSummaryData } from "common/data/demo/compliance/compliancesummary";

const MainDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [combinedData, setCombinedData] = useState({ vendors: [], software: [] });

  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj?.username;

  const COLORS = {
    Critical: '#DC3545',
    High: '#FD7E14',
    Medium: '#FFC107',
    Low: '#17A2B8',
  };

  const calculateSummaryStats = (data) => {
    const severityCounts = {
      Critical: 0,
      High: 0,
      Medium: 0,
      Low: 0
    };

    data.forEach(item => {
      severityCounts[item.severity]++;
    });

    return {
      total: data.length,
      severityCounts,
      activeVendors: data.filter(v => v.isEnabled).length
    };
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`https://api.compliance.cx/compliance/combined/${userId}`);
      const data = await response.json();
      setCombinedData(data);
    } catch (error) {
      if (userId === "demo") {
        setCombinedData(complianceSummaryData);
      }
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const vendorStats = calculateSummaryStats(combinedData.vendors || []);
  const softwareStats = calculateSummaryStats(combinedData.software || []);

  const SummaryCard = ({ title, value, icon, color }) => (
    <Col xl={3} md={6}>
      <Card className="shadow-sm">
        <CardBody>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h4 className="mb-2 font-size-16">{title}</h4>
              <h2 className="mb-0 font-size-24">{value}</h2>
            </div>
            <div className={`avatar-sm rounded-circle bg-${color} align-self-center`}>
              <span className="avatar-title rounded-circle bg-light text-primary font-size-24">
                <i className={`bx ${icon} font-size-24`}></i>
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  const RiskSummaryCard = ({ title, stats, icon }) => (
    <Col xl={6} md={6}>
      <Card className="shadow-sm h-100">
        <CardBody>
          <div className="d-flex align-items-center mb-3">
            <h4 className="card-title mb-0">{title}</h4>
            <div className="ms-auto">
              <i className={`bx ${icon} font-size-24 text-primary`}></i>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {Object.entries(stats.severityCounts).map(([severity, count]) => (
              <div key={severity} className="me-3">
                <p className="mb-1 text-muted">{severity}</p>
                <h5 className="font-size-16" style={{ color: COLORS[severity] }}>{count}</h5>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Breadcrumbs breadcrumbItem="Home" />
        <Container fluid>
          <Row>
            <SummaryCard
              title="Total Vendors"
              value={vendorStats.total}
              icon="bx-building-house"
              color="primary"
            />
            <SummaryCard
              title="Active Vendors"
              value={vendorStats.activeVendors}
              icon="bx-check-circle"
              color="success"
            />
            <SummaryCard
              title="Total Software"
              value={softwareStats.total}
              icon="bx-package"
              color="info"
            />
            <SummaryCard
              title="Active Software"
              value={softwareStats.activeVendors}
              icon="bx-cube"
              color="warning"
            />
          </Row>

          <Row className="mt-4">
            <RiskSummaryCard
              title="Vendor Risk Distribution"
              stats={vendorStats}
              icon="bx-shield-quarter"
            />
            <RiskSummaryCard
              title="Software Risk Distribution"
              stats={softwareStats}
              icon="bx-code-block"
            />
          </Row>

          <Row className="mt-4">
            <Col>
              <Card className="shadow-sm">
                <CardBody>
                  <h4 className="card-title mb-4">Risk Assessment Matrix</h4>
                  <div style={{ height: "600px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 40 }}>
                        <defs>
                          <clipPath id="quadrantClip">
                            <rect x="0" y="0" width="100%" height="100%" />
                          </clipPath>
                        </defs>

                        <g clipPath="url(#quadrantClip)">
                          <rect x="70%" y="0" width="30%" height="30%" fill="#ffebee" fillOpacity="0.2" />
                          <rect x="70%" y="30%" width="30%" height="70%" fill="#fff3e0" fillOpacity="0.2" />
                          <rect x="0" y="0" width="70%" height="30%" fill="#e3f2fd" fillOpacity="0.2" />
                          <rect x="0" y="30%" width="70%" height="70%" fill="#f1f8e9" fillOpacity="0.2" />
                        </g>

                        <CartesianGrid strokeDasharray="3 3" strokeWidth={0.5} />

                        <line x1="33%" y1="0" x2="33%" y2="100%" stroke="#666" strokeDasharray="3 3" strokeWidth={1} />
                        <line x1="66%" y1="0" x2="66%" y2="100%" stroke="#666" strokeDasharray="3 3" strokeWidth={1} />

                        <XAxis
                          type="number"
                          dataKey="adjustedImpact"
                          domain={[0, 10]}
                          label={{ value: "Impact Score", position: "bottom" }}
                          ticks={[0, 2, 4, 6, 8, 10]}
                        />
                        <YAxis
                          type="number"
                          dataKey="adjustedExploitability"
                          domain={[0, 10]}
                          label={{ value: "Risk Score", angle: -90, position: "left" }}
                          ticks={[0, 2, 4, 6, 8, 10]}
                        />
                        <Tooltip
                          content={({ payload }) => {
                            if (payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-white p-3 shadow-lg border rounded">
                                  <p className="font-bold text-lg mb-1">{data.name}</p>
                                  <p className="mb-1">Category: {data.category}</p>
                                  <p className="mb-1">Impact: {data.impact}</p>
                                  <p className="mb-1">Risk: {data.exploitability}</p>
                                  <p className="mb-1">Frameworks: {data.frameworks.join(", ")}</p>
                                  <p className="mb-0">Last Assessment: {data.lastAssessment}</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend verticalAlign="top" height={36} />
                        {["Critical", "High", "Medium", "Low"].map((severity) => (
                          <React.Fragment key={severity}>
                            <Scatter
                              name={`${severity} Vendors`}
                              data={combinedData.vendors
                                .filter((item) => item.severity === severity && item.isEnabled)
                                .map((item) => ({
                                  ...item,
                                  adjustedImpact: 0.6 * item.impact + 0.4 * item.base,
                                  adjustedExploitability: 0.6 * item.exploitability + 0.4 * item.base,
                                }))}
                              fill="none"
                              stroke={COLORS[severity]}
                              shape={(props) => {
                                const { cx, cy, payload } = props;
                                const baseSize = 8;
                                const frameworksBonus = payload.frameworks.length * 3;
                                const size = baseSize + frameworksBonus;
                                return (
                                  <circle
                                    cx={cx}
                                    cy={cy}
                                    r={size}
                                    strokeWidth={2}
                                    fill="none"
                                    stroke={COLORS[payload.severity]}
                                  />
                                );
                              }}
                            />
                            <Scatter
                              name={`${severity} Software`}
                              data={combinedData.software
                                .filter((item) => item.severity === severity && item.isEnabled)
                                .map((item) => ({
                                  ...item,
                                  adjustedImpact: 0.6 * item.impact + 0.4 * item.base,
                                  adjustedExploitability: 0.6 * item.exploitability + 0.4 * item.base,
                                }))}
                              fill={COLORS[severity]}
                              shape={(props) => {
                                const { cx, cy, payload } = props;
                                const baseSize = 8;
                                const frameworksBonus = payload.frameworks.length * 3;
                                const size = baseSize + frameworksBonus;
                                return (
                                  <circle
                                    cx={cx}
                                    cy={cy}
                                    r={size}
                                    fill={COLORS[payload.severity]}
                                    fillOpacity={0.6}
                                  />
                                );
                              }}
                            />
                          </React.Fragment>
                        ))}
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MainDashboard;